import type { BitPalmTheme } from "./types";

export type HostTheme = "light" | "dark";

const DARK_CLASSES = ["dark", "dark-mode", "dark-theme", "theme-dark"];
const LIGHT_CLASSES = ["light", "light-mode", "light-theme", "theme-light"];
const THEME_ATTRIBUTES = ["data-theme", "data-color-scheme", "data-color-mode", "data-mode", "data-bs-theme"];
const DARK_QUERY = "(prefers-color-scheme: dark)";

export function normalizeThemeSetting(value: unknown): BitPalmTheme {
  return value === "light" || value === "dark" ? value : "auto";
}

/** Theme set explicitly on an element: a class like "dark" or an attribute like data-theme="dark". */
function explicitTheme(el: Element | null): HostTheme | null {
  if (!el) return null;
  if (DARK_CLASSES.some((name) => el.classList.contains(name))) return "dark";
  if (LIGHT_CLASSES.some((name) => el.classList.contains(name))) return "light";
  for (const attr of THEME_ATTRIBUTES) {
    const value = (el.getAttribute(attr) || "").toLowerCase();
    if (value.includes("dark")) return "dark";
    if (value.includes("light")) return "light";
  }
  return null;
}

function channel(value: number): number {
  const s = value / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/** Dark or light from an element's own opaque background color. */
function backgroundTheme(el: Element | null, win: Window): HostTheme | null {
  if (!el) return null;
  const match = win.getComputedStyle(el).backgroundColor.match(/rgba?\(([^)]+)\)/);
  if (!match) return null;
  const [r, g, b, a = 1] = match[1].split(/[\s,/]+/).filter(Boolean).map(Number);
  if ([r, g, b, a].some((n) => Number.isNaN(n)) || a < 0.5) return null;
  const luminance = 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  return luminance < 0.2 ? "dark" : "light";
}

/** The site declares color-scheme: only dark, or both and the visitor's system decides. */
function colorSchemeTheme(doc: Document, win: Window): HostTheme | null {
  const computed = win.getComputedStyle(doc.documentElement).colorScheme || "";
  const meta = doc.querySelector('meta[name="color-scheme"]')?.getAttribute("content") || "";
  const scheme = `${computed} ${meta}`.toLowerCase();
  if (!scheme.includes("dark")) return null;
  if (!scheme.includes("light")) return "dark";
  return win.matchMedia?.(DARK_QUERY).matches ? "dark" : "light";
}

/**
 * Best guess of the host page's current theme, most explicit signal first:
 * theme classes and attributes on <html> or <body>, then the page background,
 * then a declared color-scheme. Pages without any of these count as light, so
 * a light-only site never gets a dark widget just because the visitor's
 * system is dark.
 */
export function detectHostTheme(doc: Document = document, win: Window = window): HostTheme {
  return (
    explicitTheme(doc.documentElement) ??
    explicitTheme(doc.body) ??
    backgroundTheme(doc.body, win) ??
    backgroundTheme(doc.documentElement, win) ??
    colorSchemeTheme(doc, win) ??
    "light"
  );
}

/**
 * Calls onChange when the detected theme changes: class or attribute changes
 * on <html> and <body>, and system theme changes. Returns a stop function.
 */
export function watchHostTheme(
  onChange: (theme: HostTheme) => void,
  doc: Document = document,
  win: Window = window,
): () => void {
  let last = detectHostTheme(doc, win);
  let scheduled = false;

  const check = () => {
    scheduled = false;
    const next = detectHostTheme(doc, win);
    if (next === last) return;
    last = next;
    onChange(next);
  };
  // Theme switches often touch several attributes at once, check once per frame.
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    if (typeof win.requestAnimationFrame === "function") win.requestAnimationFrame(check);
    else setTimeout(check, 16);
  };

  const observer = typeof MutationObserver !== "undefined" ? new MutationObserver(schedule) : null;
  const observed = { attributes: true, attributeFilter: ["class", "style", ...THEME_ATTRIBUTES] };
  observer?.observe(doc.documentElement, observed);
  if (doc.body) observer?.observe(doc.body, observed);

  const media = typeof win.matchMedia === "function" ? win.matchMedia(DARK_QUERY) : null;
  media?.addEventListener?.("change", schedule);

  return () => {
    observer?.disconnect();
    media?.removeEventListener?.("change", schedule);
  };
}
